/*
 * Category circles (sections/bdc-category-circles.liquid): a row of round category links
 * that scrolls sideways without moving the page. Native scrolling does the work (swipe,
 * trackpad, Shift+wheel, tabbing through the links); this adds the arrow buttons and edge
 * fades (shown only when there's more to see), drag-to-scroll for a mouse, and brings the
 * circle for the current page into view on load.
 */
if (!customElements.get('bdc-circles')) {
  class BdcCircles extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-bdc-circles-track]');
      if (!this.track) return;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.querySelector('[data-bdc-circles-prev]')?.addEventListener('click', () => this.page(-1));
      this.querySelector('[data-bdc-circles-next]')?.addEventListener('click', () => this.page(1));

      this.onScroll = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.update();
        });
      };
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.resizeObserver = new ResizeObserver(this.onScroll);
      this.resizeObserver.observe(this.track);

      this.enableDrag();
      this.revealCurrent();
      this.update();
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
    }

    update() {
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      this.toggleAttribute('data-can-prev', scrollLeft > 1);
      this.toggleAttribute('data-can-next', scrollLeft < scrollWidth - clientWidth - 1);
    }

    page(direction) {
      this.track.scrollBy({
        left: direction * this.track.clientWidth * 0.8,
        behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
      });
    }

    // Center the current page's circle if it starts out of view. Sets scrollLeft directly,
    // because scrollIntoView() could also scroll the page.
    revealCurrent() {
      const current = this.track.querySelector('[aria-current="page"]');
      if (!current) return;
      const track = this.track.getBoundingClientRect();
      const item = current.getBoundingClientRect();
      if (item.left >= track.left && item.right <= track.right) return;
      this.track.scrollLeft += item.left - track.left - (track.width - item.width) / 2;
    }

    // Mouse only: touch and pen already scroll the row natively.
    enableDrag() {
      let pointerId = null;
      let startX = 0;
      let startScroll = 0;
      let dragged = false;

      this.track.addEventListener('pointerdown', (event) => {
        dragged = false;
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        if (this.track.scrollWidth <= this.track.clientWidth) return;
        pointerId = event.pointerId;
        startX = event.clientX;
        startScroll = this.track.scrollLeft;
      });

      this.track.addEventListener('pointermove', (event) => {
        if (event.pointerId !== pointerId) return;
        const distance = event.clientX - startX;
        if (!dragged) {
          if (Math.abs(distance) < 6) return;
          dragged = true;
          this.track.setPointerCapture(pointerId);
          this.track.classList.add('is-dragging');
        }
        this.track.scrollLeft = startScroll - distance;
      });

      const end = (event) => {
        if (event.pointerId !== pointerId) return;
        pointerId = null;
        this.track.classList.remove('is-dragging');
        // The click that ends a drag comes right after pointerup; let it be swallowed first.
        if (dragged) setTimeout(() => (dragged = false));
      };
      this.track.addEventListener('pointerup', end);
      this.track.addEventListener('pointercancel', end);

      // A drag must not also open the link it started on.
      this.track.addEventListener(
        'click',
        (event) => {
          if (!dragged) return;
          event.preventDefault();
          event.stopPropagation();
        },
        true
      );
      // The browser's own link/image dragging would take over the gesture.
      this.track.addEventListener('dragstart', (event) => event.preventDefault());
    }
  }

  customElements.define('bdc-circles', BdcCircles);
}
